import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MovieCard from '../components/MovieCard';
import MovieCarousel from '../components/MovieCarousel';
import { moviesAPI, showtimesAPI, branchesAPI, smartRatingsAPI } from '../api';
import { Clock, Calendar, MapPin, Play, X, ChevronLeft, ChevronRight, Film, Home, Search, Edit } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { useSettings } from '../contexts/SettingsContext';
import { usePageTracking, trackEvent } from '../hooks/useAnalytics';
import { useAuth } from '../AuthContext';

// Extract YouTube video ID from URL
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  
  // Handle different YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /^([a-zA-Z0-9_-]{11})$/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  
  return null;
};

// Get YouTube thumbnail URL
const getYouTubeThumbnail = (url) => {
  const videoId = getYouTubeVideoId(url);
  if (!videoId) return 'https://via.placeholder.com/480x360?text=Video';
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
};

const getShowtimeColor = (showDate, showTime, allShowtimes, settings) => {
  const now = new Date();
  const showDateTime = new Date(`${showDate}T${showTime}`);
  const diffMs = showDateTime - now;

  // Geçmiş seanslar
  if (diffMs < 0) return { className: 'bg-gray-700/50 text-gray-500 border border-gray-600/30 cursor-not-allowed', style: {} };
  
  // Gelecekteki tüm seansları bul
  const futureShowtimes = allShowtimes
    .map(st => new Date(`${st.show_date}T${st.show_time}`))
    .filter(dt => dt >= now)
    .sort((a, b) => a - b);
  
  const gradientFrom = settings?.gradient_showtime_from || '#f59e0b';
  const gradientTo = settings?.gradient_showtime_to || '#ef4444';
  
  // En yakın seans mı? (Yeşil - özel renk)
  if (futureShowtimes.length > 0 && showDateTime.getTime() === futureShowtimes[0].getTime()) {
    return { 
      className: 'text-white border shadow-lg',
      style: {
        background: `linear-gradient(to right, #10b981, #059669)`,
        borderColor: '#10b98180',
        boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.2)'
      }
    };
  }
  
  // Diğer gelecek seanslar (Admin gradient renklerini kullan)
  return { 
    className: 'text-white border',
    style: {
      background: `linear-gradient(to right, ${gradientFrom}, ${gradientTo})`,
      borderColor: `${gradientFrom}4d`
    }
  };
};

// Seanslari salon+format+dil kombinasyonuna gore grupla
const groupShowtimesByHallFormatLanguage = (showtimesList) => {
  const groups = {};
  showtimesList.forEach((st) => {
    const key = `${st.hall_number || ''}|${st.format || ''}|${st.language || ''}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(st);
  });
  Object.values(groups).forEach((arr) => {
    arr.sort((a, b) => a.show_time.localeCompare(b.show_time));
  });
  return groups;
};

// Calculate days until release date
const getDaysUntilRelease = (releaseDate) => {
  if (!releaseDate) return null;
  
  try {
    const today = new Date();
    let release;
    
    // Handle Turkish date format: "7 Kasım 2025"
    if (releaseDate.includes('Kasım') || releaseDate.includes('Ekim') || releaseDate.includes('Eylül') || releaseDate.includes('Ocak') || releaseDate.includes('Şubat') || releaseDate.includes('Mart') || releaseDate.includes('Nisan') || releaseDate.includes('Mayıs') || releaseDate.includes('Haziran') || releaseDate.includes('Temmuz') || releaseDate.includes('Ağustos') || releaseDate.includes('Aralık')) {
      // Convert Turkish months to English
      const turkishToEnglish = {
        'Ocak': 'January', 'Şubat': 'February', 'Mart': 'March',
        'Nisan': 'April', 'Mayıs': 'May', 'Haziran': 'June',
        'Temmuz': 'July', 'Ağustos': 'August', 'Eylül': 'September',
        'Ekim': 'October', 'Kasım': 'November', 'Aralık': 'December'
      };
      
      let englishDate = releaseDate;
      Object.keys(turkishToEnglish).forEach(turkishMonth => {
        englishDate = englishDate.replace(turkishMonth, turkishToEnglish[turkishMonth]);
      });
      
      release = new Date(englishDate);
    } else {
      release = new Date(releaseDate);
    }
    
    // Check if date is valid
    if (isNaN(release.getTime())) {
      return null;
    }
    
    // Remove time part for accurate day calculation
    today.setHours(0, 0, 0, 0);
    release.setHours(0, 0, 0, 0);
    
    const diffTime = release - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  } catch (error) {
    console.error('Date calculation error:', error);
    return null;
  }
};

const MovieDetail = () => {
  const { id, slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings } = useSettings();
  const [movie, setMovie] = useState(null);
  const [otherMovies, setOtherMovies] = useState([]);
  const [showtimes, setShowtimes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [smartRatingOptions, setSmartRatingOptions] = useState([]);
  
  // Get today's date in Turkey timezone (UTC+3)
  const getTodayDate = () => {
    const now = new Date();
    // Convert to Turkey time (UTC+3)
    const turkeyTime = new Date(now.getTime() + (3 * 60 * 60 * 1000));
    const year = turkeyTime.getUTCFullYear();
    const month = String(turkeyTime.getUTCMonth() + 1).padStart(2, '0');
    const day = String(turkeyTime.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showTrailer, setShowTrailer] = useState(false);
  const [selectedTrailer, setSelectedTrailer] = useState(null);

  // Analytics tracking
  usePageTracking(movie ? `${movie.title} - Film Detayı` : 'Film Detayı');

  // Update title when movie loads
  useEffect(() => {
    if (movie) {
      document.title = `${movie.title} - ${settings.site_name || 'Cinefora'}`;
      
      // Track movie view
      trackEvent('movie_view', 'movies', {
        movie_id: movie._id,
        title: movie.title,
        genres: movie.genres,
        status: movie.status
      });
    }
  }, [movie, settings.site_name]);

  useEffect(() => {
    loadMovieData();
    loadSmartRatings();
  }, [id, slug, selectedDate, selectedBranch]);
  
  const loadSmartRatings = async () => {
    try {
      const response = await smartRatingsAPI.getAll();
      setSmartRatingOptions(response.data);
    } catch (error) {
      console.error('Error loading smart ratings:', error);
    }
  };

  const loadMovieData = async () => {
    try {
      const movieIdentifier = slug || id;
      const [movieRes, branchesRes] = await Promise.all([
        moviesAPI.getById(movieIdentifier),
        branchesAPI.getAll(),
      ]);

      setMovie(movieRes.data);
      setBranches(branchesRes.data);

      // Load other now playing movies (excluding current movie)
      const nowPlayingRes = await moviesAPI.getWithShowtimes();
      const otherNowPlayingMovies = nowPlayingRes.data.filter(m => 
        m._id !== movieRes.data._id
      );
      setOtherMovies(otherNowPlayingMovies.slice(0, 8)); // Limit to 8 movies
      
      // Load showtimes using movie ID
      const showtimesRes = await showtimesAPI.getAll({ 
        movie_id: movieRes.data._id, 
        show_date: selectedDate 
      });
      
      let filteredShowtimes = showtimesRes.data;
      if (selectedBranch !== 'all') {
        filteredShowtimes = filteredShowtimes.filter(s => s.branch_id === selectedBranch);
      }
      setShowtimes(filteredShowtimes);
    } catch (error) {
      console.error('Error loading movie data:', error);
      // Film bulunamadıysa ana sayfaya yönlendir
      if (error.response && error.response.status === 404) {
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  const getBranchName = (branchId) => {
    return branches.find(b => b._id === branchId)?.name || 'Bilinmiyor';
  };

  // Get smart rating details
  const getSmartRatingDetails = (ratingIdOrLabel) => {
    let rating = smartRatingOptions.find(r => r._id === ratingIdOrLabel);
    if (!rating) {
      rating = smartRatingOptions.find(r => r.label === ratingIdOrLabel);
    }
    return rating;
  };

  const groupedShowtimes = showtimes.reduce((acc, showtime) => {
    if (!acc[showtime.branch_id]) {
      acc[showtime.branch_id] = [];
    }
    acc[showtime.branch_id].push(showtime);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Header />
        <div className="container mx-auto px-6 py-32 text-center">
          <p className="text-white text-xl">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!movie) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex flex-col">
        <Helmet>
          <title>Film Bulunamadı - Cinefora</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        
        <Header />
        
        <main className="flex-grow flex items-center justify-center px-4 py-12">
          <div className="max-w-2xl w-full text-center space-y-8">
            {/* 404 Icon */}
            <div className="relative">
              <div className="text-9xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500">
                404
              </div>
              <Film className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 text-gray-800 opacity-30" />
            </div>

            {/* Message */}
            <div className="space-y-4">
              <h1 className="text-3xl md:text-4xl font-bold text-white">
                Film Bulunamadı
              </h1>
              <p className="text-gray-400 text-lg">
                Aradığınız film sinemalarımızdan kaldırılmış veya hiç var olmamış olabilir.
              </p>
              <p className="text-gray-500 text-sm">
                2 saniye içinde otomatik olarak ana sayfaya yönlendirileceksiniz...
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-semibold px-8 py-3 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <Home className="w-5 h-5" />
                Ana Sayfaya Dön
              </button>
              
              <button
                onClick={() => navigate('/vizyondaki-filmler')}
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white font-semibold px-8 py-3 rounded-lg transition-all duration-300 border border-gray-700"
              >
                <Search className="w-5 h-5" />
                Vizyondaki Filmler
              </button>
            </div>

            {/* Popular Links */}
            <div className="pt-8 border-t border-gray-800">
              <p className="text-gray-500 text-sm mb-4">Popüler Kategoriler:</p>
              <div className="flex flex-wrap justify-center gap-3">
                <button
                  onClick={() => navigate('/tur/aksiyon')}
                  className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition-colors"
                >
                  Aksiyon
                </button>
                <button
                  onClick={() => navigate('/tur/komedi')}
                  className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition-colors"
                >
                  Komedi
                </button>
                <button
                  onClick={() => navigate('/tur/korku')}
                  className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition-colors"
                >
                  Korku
                </button>
                <button
                  onClick={() => navigate('/yakinda')}
                  className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition-colors"
                >
                  Yakında
                </button>
              </div>
            </div>
          </div>
        </main>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Helmet>
        <title>{movie.title} - {settings.site_name || 'Cinefora'}</title>
        <meta name="description" content={`${movie.title} filmi hakkında detaylı bilgi, fragman, seanslar ve bilet satış bilgileri. ${movie.synopsis ? movie.synopsis.substring(0, 150) : ''}`} />
        <meta name="keywords" content={`${movie.title}, film, sinema, vizyon, ${movie.genres?.join(', ')}`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="video.movie" />
        <meta property="og:url" content={`https://cinefora.com.tr/film/${movie.slug || movie._id}`} />
        <meta property="og:title" content={`${movie.title} - ${settings.site_name || 'Cinefora'}`} />
        <meta property="og:description" content={movie.synopsis ? movie.synopsis.substring(0, 200) : `${movie.title} filmi ${settings.site_name || 'Cinefora'}'da vizyonda.`} />
        <meta property="og:image" content={movie.poster || movie.header_image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content={settings.site_name || 'Cinefora Sinemaları'} />
        <meta property="og:locale" content="tr_TR" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`https://cinefora.com.tr/film/${movie.slug || movie._id}`} />
        <meta property="twitter:title" content={`${movie.title} - ${settings.site_name || 'Cinefora'}`} />
        <meta property="twitter:description" content={movie.synopsis ? movie.synopsis.substring(0, 200) : `${movie.title} filmi ${settings.site_name || 'Cinefora'}'da vizyonda.`} />
        <meta property="twitter:image" content={movie.poster || movie.header_image} />
        
        {/* Movie Specific */}
        {movie.release_date && <meta property="video:release_date" content={movie.release_date} />}
        {movie.directors && movie.directors.length > 0 && <meta property="video:director" content={movie.directors.join(', ')} />}
        {movie.actors && movie.actors.length > 0 && <meta property="video:actor" content={movie.actors.slice(0, 3).join(', ')} />}
        {movie.duration && <meta property="video:duration" content={movie.duration.toString()} />}
      </Helmet>
      <Header />

      {/* Movie Hero Section with Header Image */}
      <div className="relative">
        {/* Hero Background Image */}
        <div
          className="h-[700px] md:h-[650px] bg-cover bg-center relative"
          style={{
            backgroundImage: `url(${movie.header_image || movie.poster})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-black/70 to-black/40" />
          
          {/* Movie Info Overlay on Header */}
          <div className="absolute bottom-0 left-0 right-0 pb-16 md:pb-8">
            <div className="container mx-auto px-4 md:px-6">
              <div className="flex flex-col md:flex-row gap-4 md:gap-8 items-start md:items-end">
                {/* Poster */}
                <img
                  src={movie.poster}
                  alt={movie.title}
                  className="w-36 md:w-72 h-56 md:h-[430px] object-cover rounded-lg shadow-2xl border-4 border-white/10"
                />
                
                {/* Movie Info */}
                <div className="flex-1 text-white pb-4">
                  <h1 className="text-4xl md:text-6xl font-bold mb-2 drop-shadow-2xl">{movie.title}</h1>
                  {user && (
                    <button
                      onClick={() => navigate(`/admin/movies/edit/${movie._id}`)}
                      className="inline-flex items-center gap-1.5 text-sm text-gray-300 hover:text-[#FDB714] bg-black/30 hover:bg-black/50 px-3 py-1.5 rounded-lg border border-white/10 mb-3 transition-colors"
                    >
                      <Edit size={14} />
                      Duzenle
                    </button>
                  )}
                  
                  {/* Original Title */}
                  {movie.original_title && (
                    <p className="text-xl md:text-2xl text-gray-300 mb-4 italic">{movie.original_title}</p>
                  )}
                  
                  <div className="flex flex-wrap gap-4 mb-4 text-base md:text-lg">
                    <span className="flex items-center gap-2 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-lg">
                      <Clock size={20} />
                      {movie.duration} dk.
                    </span>
                    
                    {/* Release Date with Countdown */}
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-2 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-lg">
                        <Calendar size={20} />
                        {movie.release_date}
                      </span>
                      
                      {/* Days Countdown Pill */}
                      {(() => {
                        const daysLeft = getDaysUntilRelease(movie.release_date);
                        if (daysLeft > 0) {
                          return (
                            <span className="bg-white text-gray-900 px-3 py-1.5 rounded-full text-sm font-bold shadow-lg border border-gray-200">
                              SON {daysLeft} GÜN!
                            </span>
                          );
                        } else if (daysLeft === 0) {
                          return (
                            <span className="bg-green-500 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg">
                              BUGÜN VİZYONDA!
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                  
                  {/* Genres */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {movie.genres.map((genre, index) => (
                      <Link
                        key={index}
                        to={`/tur/${genre.toLowerCase().replace(/\s+/g, '-')}`}
                        className="bg-[#FDB714] hover:bg-[#e5a510] text-black px-3 py-1 rounded-full text-sm font-semibold transition-colors duration-200 cursor-pointer"
                      >
                        {genre}
                      </Link>
                    ))}
                  </div>
                  
                  {/* Formats */}
                  {movie.formats && movie.formats.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {movie.formats.map((format, index) => (
                        <span key={index} className="bg-white/10 backdrop-blur-sm text-white border border-white/30 px-3 py-1 rounded-lg text-sm font-medium">
                          {format}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Below Header */}
        <div className="container mx-auto px-4 md:px-6 py-8">
          
          {/* Synopsis */}
          <div className="mb-8">
            <h3 className="text-white text-xl font-bold mb-4">Film Konusu</h3>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">{movie.synopsis}</p>
          </div>
          
          {/* Film Detayları (Sadece dolu olanlar) */}
          {(movie.director || movie.cast || movie.producer || movie.distributor || movie.country || movie.importer || movie.second_release_date) && (
            <div className="mb-8">
              <h3 className="text-white text-xl font-bold mb-4">Film Detayları</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {movie.director && (
                  <div className="flex gap-3 items-start">
                    <span className="text-gray-400 font-semibold min-w-[120px]">Yönetmen:</span>
                    <div className="text-white">
                      {movie.director.split(',').map((director, index) => (
                        <span key={index}>
                          {index > 0 && ', '}
                          <Link 
                            to={`/kisi/${encodeURIComponent(director.trim())}?type=director`}
                            className="hover:text-[#FDB714] transition-colors underline decoration-dotted"
                          >
                            {director.trim()}
                          </Link>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {movie.cast && (
                  <div className="flex gap-3 items-start md:col-span-2">
                    <span className="text-gray-400 font-semibold min-w-[120px]">Oyuncular:</span>
                    <div className="text-white">
                      {movie.cast.split(',').map((actor, index) => (
                        <span key={index}>
                          {index > 0 && ', '}
                          <Link 
                            to={`/kisi/${encodeURIComponent(actor.trim())}?type=cast`}
                            className="hover:text-[#FDB714] transition-colors underline decoration-dotted"
                          >
                            {actor.trim()}
                          </Link>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {movie.country && (
                  <div className="flex gap-3 items-start">
                    <span className="text-gray-400 font-semibold min-w-[120px]">Ülke:</span>
                    <span className="text-white">{movie.country}</span>
                  </div>
                )}
                {movie.producer && (
                  <div className="flex gap-3 items-start">
                    <span className="text-gray-400 font-semibold min-w-[120px]">Yapımcı:</span>
                    <span className="text-white">{movie.producer}</span>
                  </div>
                )}
                {movie.distributor && (
                  <div className="flex gap-3 items-start">
                    <span className="text-gray-400 font-semibold min-w-[120px]">Dağıtım:</span>
                    <span className="text-white">{movie.distributor}</span>
                  </div>
                )}
                {movie.importer && (
                  <div className="flex gap-3 items-start">
                    <span className="text-gray-400 font-semibold min-w-[120px]">İthalatçı:</span>
                    <span className="text-white">{movie.importer}</span>
                  </div>
                )}
                {movie.second_release_date && (
                  <div className="flex gap-3 items-start">
                    <span className="text-gray-400 font-semibold min-w-[120px]">2. Vizyon:</span>
                    <span className="text-white">{movie.second_release_date}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Film Fragmanları */}
          {(movie.trailers && movie.trailers.length > 0) ? (
            <div className="mb-8">
              <h3 className="text-white text-xl font-bold mb-4">Film Fragmanları</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {movie.trailers.map((trailer, index) => {
                  const thumbnailUrl = getYouTubeThumbnail(trailer.url);
                  return (
                    <div 
                      key={index}
                      onClick={() => {
                        setSelectedTrailer(trailer);
                        setShowTrailer(true);
                      }}
                      className="relative group cursor-pointer rounded-lg overflow-hidden bg-gray-800 border border-gray-700 hover:border-[#FDB714] transition-all"
                    >
                      {/* Thumbnail */}
                      <div className="relative aspect-video">
                        <img 
                          src={thumbnailUrl}
                          alt={trailer.title}
                          className="w-full h-full object-cover"
                        />
                        {/* Play Overlay */}
                        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-all flex items-center justify-center">
                          <div className="bg-[#FDB714] rounded-full p-4 group-hover:scale-110 transition-transform">
                            <Play size={32} className="text-black fill-black" />
                          </div>
                        </div>
                      </div>
                      {/* Title */}
                      <div className="p-3 bg-gray-900">
                        <p className="text-white font-semibold text-sm line-clamp-2">{trailer.title}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : movie.trailer ? (
            // Eski tek fragman için backward compatibility
            <div className="mb-8">
              <Button 
                onClick={() => {
                  setSelectedTrailer({ title: 'Fragman', url: movie.trailer });
                  setShowTrailer(true);
                }}
                className="bg-[#FDB714] hover:bg-[#e5a510] text-black font-bold px-8 py-6 text-base flex items-center gap-2"
              >
                <Play size={24} />
                Fragmanı İzle
              </Button>
            </div>
          ) : null}

          {/* Smart Ratings */}
          {movie.smartRatings && movie.smartRatings.length > 0 && smartRatingOptions.length > 0 && (
            <div className="mb-8">
              <h3 className="text-white text-xl font-bold mb-4">Derecelendirme ve Akıllı İşaretler</h3>
              <div className="flex flex-wrap gap-3">
                {movie.smartRatings.map((ratingIdOrLabel, index) => {
                  let ratingDetails = smartRatingOptions.find(r => r._id === ratingIdOrLabel);
                  if (!ratingDetails) {
                    ratingDetails = smartRatingOptions.find(r => r.label === ratingIdOrLabel);
                  }
                  if (!ratingDetails) return null;
                  
                  return (
                    <div 
                      key={index}
                      className="flex items-center gap-2 bg-gray-900 border border-gray-800 hover:border-[#FDB714] px-4 py-3 rounded-lg transition-all"
                    >
                      <div className="w-10 h-10 bg-white rounded p-1.5">
                        <img 
                          src={ratingDetails.icon} 
                          alt={ratingDetails.label}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <span className="text-sm font-semibold text-white">{ratingDetails.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Old code below - will clean up */}
          <div className="hidden">
            </div>
          </div>
        </div>

      {/* Showtimes Section */}
      <div className="container mx-auto px-4 md:px-6 py-8 md:py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 md:mb-8">Seanslar</h2>

        <div className="flex flex-col md:flex-row gap-4 mb-6 md:mb-8">
          <div className="flex-1">
            <label className="text-gray-400 text-sm mb-2 block">Tarih Seçin</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
            />
          </div>
          <div className="flex-1">
            <label className="text-gray-400 text-sm mb-2 block">Şube Seçin</label>
            <select
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
            >
              <option value="all">Tüm Şubeler</option>
              {branches.map((branch) => (
                <option key={branch._id} value={branch._id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {Object.keys(groupedShowtimes).length === 0 ? (
          <p className="text-gray-400 text-center py-12">Bu tarih için seans bulunamadı.</p>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedShowtimes).map(([branchId, branchShowtimes]) => (
              <div key={branchId} className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-xl p-6 border border-orange-500/20 hover:border-orange-500/40 transition-all">
                <h3 className="text-white font-bold text-xl mb-4 flex items-center gap-2">
                  <MapPin size={20} className="text-[#FDB714]" />
                  {getBranchName(branchId)}
                </h3>
                <div className="space-y-4">
                  {Object.entries(groupShowtimesByHallFormatLanguage(branchShowtimes)).map(([groupKey, groupShowtimes]) => {
                    const first = groupShowtimes[0];
                    const labelParts = [first.hall_number, [first.format, first.language].filter(Boolean).join(' / ')].filter(Boolean);
                    return (
                      <div key={groupKey}>
                        <h4 className="text-gray-300 font-semibold text-sm mb-2">
                          {labelParts.join(' \u2022 ')}
                        </h4>
                        <div className="flex flex-wrap gap-3">
                          {groupShowtimes.map((showtime) => {
                            const colorStyle = getShowtimeColor(showtime.show_date, showtime.show_time, showtimes, settings);
                            return (
                              <Button
                                key={showtime._id}
                                className={`px-6 py-3 font-semibold ${colorStyle.className}`}
                                style={colorStyle.style}
                                disabled={colorStyle.className.includes('gray')}
                              >
                                {showtime.show_time}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Other Movies Carousel - Before Footer */}
      {otherMovies.length > 0 && (
        <section className="bg-gray-900 py-16">
          <div className="container mx-auto px-4 md:px-6">
            <div className="mb-8">
              <h2 className="text-white text-3xl font-bold">Vizyondaki Diğer Filmler</h2>
            </div>
            
            <MovieCarousel movies={otherMovies} autoplayDelay={5000} />
          </div>
        </section>
      )}

      <Footer />
      
      {/* Trailer Modal */}
      {showTrailer && selectedTrailer && (
        <Dialog open={showTrailer} onOpenChange={(open) => {
          setShowTrailer(open);
          if (!open) setSelectedTrailer(null);
        }}>
          <DialogContent className="max-w-4xl w-full bg-black border-gray-800 p-0">
            <button
              onClick={() => {
                setShowTrailer(false);
                setSelectedTrailer(null);
              }}
              className="absolute top-4 right-4 z-50 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
            >
              <X size={24} />
            </button>
            {/* Trailer Title */}
            {selectedTrailer.title && (
              <div className="absolute top-4 left-4 z-50 bg-black/70 text-white px-4 py-2 rounded-lg">
                <p className="font-bold">{selectedTrailer.title}</p>
              </div>
            )}
            <div className="relative pt-[56.25%]">
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src={`https://www.youtube.com/embed/${getYouTubeVideoId(selectedTrailer.url)}?autoplay=1`}
                title={selectedTrailer.title || `${movie.title} Fragman`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Helper function to extract YouTube video ID
const extractYouTubeId = (url) => {
  if (!url) return '';
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : url;
};

export default MovieDetail;
